import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbDateParserFormatter, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { Title } from '@angular/platform-browser';

import { ModalFinishByPhoneComponent } from 'src/app/components/modals/modal-finish-by-phone/modal-finish-by-phone.component';
import { BranchService } from 'src/app/services/branch/branch.service';
import { LoanService } from 'src/app/services/loan/loan.service';
import { FinalLoanApplication } from 'src/app/services/loan/loan.model';
import {
  DropdownMenus,
  RequestCreditType,
} from 'src/app/services/dropdowns/dropdowns.model';
import { DropdownsService } from 'src/app/services/dropdowns/dropdowns.service';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { ErrorMessageService } from 'src/app/services/error-message/error-message.service';
import { handleErrorMessage } from 'src/app/helpers/error-handler.helper';
import { DataLayerService } from 'src/app/services/analytics/data-layer.service';
import { socialSecurityNumberValidator } from 'src/app/directives/ssn/ssn.directive';
import { RouteName } from '../loan-routing.module';
import { ApplicantBankingForm } from 'src/app/models/loan-form.model';

@Component({
  selector: 'app-bank',
  templateUrl: './bank.component.html',
  styleUrls: ['./bank.component.scss'],
})
export class BankComponent implements OnInit, OnDestroy {
  bankingFormGroup: FormGroup;
  coApplicantBankingFormGroup: FormGroup;
  loanApplication: FinalLoanApplication | null = null;
  coApplicant = false;
  dropdownMenus: DropdownMenus | null = null;
  isSaving = false;
  isDirtyAndMissingRequiredFields = false;
  isCoAppFormDirtyAndMissingRequiredFields = false;

  private subscriptions: Array<Subscription> = [];

  constructor(
    private formBuilder: FormBuilder,
    private loanService: LoanService,
    private navService: NavigationService,
    private branchService: BranchService,
    private router: Router,
    private modalService: NgbModal,
    private dropdownsService: DropdownsService,
    private ngbDateParserService: NgbDateParserFormatter,
    private errorMessageService: ErrorMessageService,
    private titleService: Title,
    private dataLayerService: DataLayerService
  ) {
    this.titleService.setTitle('Eagle Loan | Bank Information');
    if (this.router.url.includes('applicant')) {
      this.navService.setBackNav(
        'Applicant Employer',
        RouteName.applicantEmployer
      );
    } else {
      this.navService.setBackNav(
        'Co-Applicant Employer',
        RouteName.coApplicantEmployer
      );
    }

    this.branchService.setShowInfoButton(true);

    this.bankingFormGroup = this.formBuilder.group({
      CheckingAccountStatus: ['', Validators.required],
      CheckingBankName: ['', Validators.required],
      HasDebitCard: ['', Validators.required],
      DateOfBirthYear: ['', Validators.required],
      DateOfBirthMonth: ['', Validators.required],
      DateOfBirthDay: ['', Validators.required],
      DateOfBirth: [''],
      SSN: ['', [Validators.required, socialSecurityNumberValidator]],
      CreditReportAuthorization: [
        { value: '', disabled: true },
        Validators.requiredTrue,
      ],
      // {DisclaimerForOhioApplicants} is no longer needed. Setting as disabled to hide from UI.
      DisclaimerForOhioApplicants: [
        { value: '', disabled: true },
        Validators.requiredTrue,
      ],
    });

    this.coApplicantBankingFormGroup = this.formBuilder.group({
      DateOfBirthYear: ['', Validators.required],
      DateOfBirthMonth: ['', Validators.required],
      DateOfBirthDay: ['', Validators.required],
      DateOfBirth: [''],
      SSN: ['', [Validators.required, socialSecurityNumberValidator]],
    });
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.loanService.finalLoanApplication.subscribe(
        (data: FinalLoanApplication | null) => {
          if (data) {
            this.initializeForm(data);
          }
        }
      )
    );

    this.subscriptions.push(
      this.dropdownsService.dropdownMenus.subscribe(
        (data: DropdownMenus | null) => {
          this.dropdownMenus = null;

          if (data) {
            this.dropdownMenus = { ...data };
          }
        }
      )
    );

    this.subscriptions.push(
      this.bankingFormGroup.statusChanges.subscribe((status: string) => {
        if (status && status === 'VALID') {
          this.isDirtyAndMissingRequiredFields = false;
        }
      })
    );

    this.subscriptions.push(
      this.coApplicantBankingFormGroup.statusChanges.subscribe(
        (status: string) => {
          if (status && status === 'VALID') {
            this.isCoAppFormDirtyAndMissingRequiredFields = false;
          }
        }
      )
    );

    // On refresh of the page it will redirect to start of application.
    if (!this.loanApplication) {
      this.router.navigateByUrl(RouteName.default, { replaceUrl: true });
    }

    if (!this.dropdownMenus) {
      this.dropdownsService.getDropdowns();
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  initializeForm(data: FinalLoanApplication) {
    this.loanApplication = { ...data };

    if (
      data.LoanApplication &&
      data.LoanApplication.RequestCreditTypeCD === RequestCreditType.coApplicant
    ) {
      this.coApplicant = true;
    }

    // Disable banking questions until account standing is selected.
    this.onCheckingAccountStandingChange('FALSE', this.bankingFormGroup);

    if (data.Applicant) {
      this.setFormValues(data.Applicant, this.bankingFormGroup);

      if (data.Applicant.DateOfBirth) {
        const dob = data.Applicant.DateOfBirth.split('-');

        this.bankingFormGroup.controls['DateOfBirth'].setValue(
          data.Applicant.DateOfBirth
        );
        this.bankingFormGroup.controls['DateOfBirthYear'].setValue(dob[0]);
        this.bankingFormGroup.controls['DateOfBirthMonth'].setValue(dob[1]);
        this.bankingFormGroup.controls['DateOfBirthDay'].setValue(dob[2]);

        // Convert string to {NgbDateStruct} and set control.
        // const dob = this.ngbDateParserService.parse(data.Applicant.DateOfBirth);

        // this.bankingFormGroup.controls['DateOfBirth'].setValue(dob);
      }
    }

    this.setFormControls(!this.coApplicant);

    // Check for Co-Applicant data.
    if (this.coApplicant) {
      if (data.CoApplicant) {
        this.setFormValues(data.CoApplicant, this.coApplicantBankingFormGroup);

        if (data.CoApplicant.DateOfBirth) {
          const dob = data.CoApplicant.DateOfBirth.split('-');

          this.coApplicantBankingFormGroup.controls['DateOfBirth'].setValue(
            data.CoApplicant.DateOfBirth
          );
          this.coApplicantBankingFormGroup.controls['DateOfBirthYear'].setValue(
            dob[0]
          );
          this.coApplicantBankingFormGroup.controls[
            'DateOfBirthMonth'
          ].setValue(dob[1]);
          this.coApplicantBankingFormGroup.controls['DateOfBirthDay'].setValue(
            dob[2]
          );

          // Convert string to {NgbDateStruct} and set control.
          // const dob = this.ngbDateParserService.parse(
          //   data.CoApplicant.DateOfBirth
          // );

          // this.coApplicantBankingFormGroup.controls['DateOfBirth'].setValue(
          //   dob
          // );
        }
      }
    }

    if (data.LoanApplication) {
      this.setFormValues(data.LoanApplication, this.bankingFormGroup);
    }

    if (data.ApplicantAdditionalIncome) {
      this.setFormValues(data.ApplicantAdditionalIncome, this.bankingFormGroup);
    }

    if (data.ApplicantBanking) {
      this.setFormValues(data.ApplicantBanking, this.bankingFormGroup);

      if (data.ApplicantBanking.CheckingAccountStatus === 'TRUE') {
        // This will enable the banking fields.
        this.onCheckingAccountStandingChange('TRUE', this.bankingFormGroup);
      }
    }
  }

  setFormValues(data: any, formGroup: FormGroup) {
    // Set form values
    Object.keys(data).forEach((key) => {
      // Check if key exists on current form.
      if (formGroup.controls[key]) {
        formGroup.controls[key].setValue(data?.[key]);
      }
    });
  }

  /**
   * Disable {FormControl}s on {coApplicantBankingFormGroup} if no co-applicant.
   *
   * @param disable Defaults to `false`. Will disable the control if `true` otherwise it will be enabled.
   */
  setFormControls(disable = false) {
    Object.keys(this.coApplicantBankingFormGroup.controls).forEach((key) => {
      if (disable) {
        this.coApplicantBankingFormGroup.controls[key].disable();
      } else {
        this.coApplicantBankingFormGroup.controls[key].enable();
      }
    });
  }

  /**
   * Change event - If a user doesn't have an account in good standing disable
   * the other bank related questions.
   *
   * @param value `TRUE` or `FALSE`
   * @param formGroup The {FormGroup} to disable/enable controls on. `bankingFormGroup` or `coApplicantBankingFormGroup`.
   */
  onCheckingAccountStandingChange(value: string, formGroup: FormGroup) {
    const bankNameCtrl = formGroup.controls['CheckingBankName'];
    const debitCardCtrl = formGroup.controls['HasDebitCard'];

    if (value === 'FALSE') {
      bankNameCtrl.disable();
      bankNameCtrl.setValue('');
      debitCardCtrl.disable();
      debitCardCtrl.setValue('');

      return;
    }

    bankNameCtrl.enable();
    debitCardCtrl.enable();
  }

  async onFinishByPhone(): Promise<void> {
    this.dataLayerService.logEvent(
      'button_clicked',
      'loan_application',
      'click',
      'Finish By Phone | Bank'
    );

    const modalRef = this.modalService.open(ModalFinishByPhoneComponent);
    const appFG = this.bankingFormGroup;
    const coAppFG = this.coApplicantBankingFormGroup;

    const appDOB =
      appFG.value.DateOfBirthYear &&
      appFG.value.DateOfBirthMonth &&
      appFG.value.DateOfBirthDay
        ? `${appFG.value.DateOfBirthYear}-${appFG.value.DateOfBirthMonth}-${appFG.value.DateOfBirthDay}`
        : '';

    const coAppDOB =
      coAppFG.value.DateOfBirthYear &&
      coAppFG.value.DateOfBirthMonth &&
      coAppFG.value.DateOfBirthDay
        ? `${coAppFG.value.DateOfBirthYear}-${coAppFG.value.DateOfBirthMonth}-${coAppFG.value.DateOfBirthDay}`
        : '';

    // const appDOB = this.bankingFormGroup.value.DateOfBirthYear
    //   ? this.ngbDateParserService.format(
    //       this.bankingFormGroup.value.DateOfBirth
    //     )
    //   : '';

    // const coAppDOB = this.coApplicantBankingFormGroup.value.DateOfBirth
    //   ? this.ngbDateParserService.format(
    //       this.coApplicantBankingFormGroup.value.DateOfBirth
    //     )
    //   : '';

    const coApplicantData: ApplicantBankingForm = {
      ...coAppFG.value,
      DateOfBirth: coAppDOB,
    };

    modalRef.componentInstance.loan = this.loanService.setApplicantBanking(
      {
        ...appFG.value,
        DateOfBirth: appDOB,
      },
      this.coApplicant ? coApplicantData : null
    );

    const result = await modalRef.result;

    if (result && result.status === 'confirmed') {
      this.onConfirmed();
    }
  }

  onConfirmed() {
    this.loanService.clearApplication();
    this.router.navigateByUrl(RouteName.skippedConfirmation);
  }

  async onNext(): Promise<void> {
    const appFG = this.bankingFormGroup;
    const coAppFG = this.coApplicantBankingFormGroup;

    this.isDirtyAndMissingRequiredFields = false;
    this.isCoAppFormDirtyAndMissingRequiredFields = false;

    if (appFG.invalid || coAppFG.invalid) {
      if (appFG.invalid) {
        appFG.markAllAsTouched();
        this.isDirtyAndMissingRequiredFields = true;
      }

      if (coAppFG.invalid) {
        coAppFG.markAllAsTouched();
        this.isCoAppFormDirtyAndMissingRequiredFields = true;
      }

      return;
    }

    this.dataLayerService.logEvent(
      'button_clicked',
      'loan_application',
      'click',
      'Submit Application'
    );

    const coApplicantData: ApplicantBankingForm = {
      ...coAppFG.value,
      DateOfBirth: `${coAppFG.value.DateOfBirthYear}-${coAppFG.value.DateOfBirthMonth}-${coAppFG.value.DateOfBirthDay}`,
    };

    const loan = this.loanService.setApplicantBanking(
      {
        ...appFG.value,
        DateOfBirth: `${appFG.value.DateOfBirthYear}-${appFG.value.DateOfBirthMonth}-${appFG.value.DateOfBirthDay}`,
      },
      this.coApplicant ? coApplicantData : null
    );

    await this.saveLoanApplication(loan);
  }

  private async saveLoanApplication(loan: FinalLoanApplication): Promise<void> {
    try {
      this.isSaving = true;

      const response = await this.loanService.postLoanApplication(
        loan,
        'loan/final'
      );

      if (response) {
        this.loanService.clearApplication();
        this.router.navigateByUrl(RouteName.confirmation);
      }

      return;
    } catch (error) {
      this.errorMessageService.addToErrorMessages(handleErrorMessage(error));
    } finally {
      this.isSaving = false;
    }
  }
}
